import Vue from 'vue'
import { isSamePath as _isSamePath, joinURL, normalizeURL, withQuery, withoutTrailingSlash } from 'ufo'

// window.{{globals.loadedCallback}} hook
// Useful for jsdom testing or plugins (https://github.com/tmpvar/jsdom#dealing-with-asynchronous-script-loading)
if (process.client) {
  window.onNuxtReadyCbs = []
  window.onNuxtReady = (cb) => {
    window.onNuxtReadyCbs.push(cb)
  }
}

export function createGetCounter (counterObject, defaultKey = '') {
  return function getCounter (id = defaultKey) {
    if (counterObject[id] === undefined) {
      counterObject[id] = 0
    }
    return counterObject[id]++
  }
}

export function empty () {}

export function globalHandleError (error) {
  if (Vue.config.errorHandler) {
    Vue.config.errorHandler(error)
  }
}

export function interopDefault (promise) {
  return promise.then(m => m.default || m)
}

export function hasFetch(vm) {
  return vm.$options && typeof vm.$options.fetch === 'function' && !vm.$options.fetch.length
}
export function purifyData(data) {
  if (process.env.NODE_ENV === 'production') {
    return data
  }

  return Object.entries(data).filter(
    ([key, value]) => {
      const valid = !(value instanceof Function) && !(value instanceof Promise)
      if (!valid) {
        console.warn(`${key} is not able to be stringified. This will break in a production environment.`)
      }
      return valid
    }
    ).reduce((obj, [key, value]) => {
      obj[key] = value
      return obj
    }, {})
}
export function getChildrenComponentInstancesUsingFetch(vm, instances = []) {
  const children = vm.$children || []
  for (const child of children) {
    if (child.$fetch) {
      instances.push(child)
    }
    if (child.$children) {
      getChildrenComponentInstancesUsingFetch(child, instances)
    }
  }
  return instances
}

export function applyAsyncData (Component, asyncData) {
  if (
    // For SSR, we once all this function without second param to just apply asyncData
    // Prevent doing this for each SSR request
    !asyncData && Component.options.__hasNuxtData
  ) {
    return
  }

  const ComponentData = Component.options._originDataFn || Component.options.data || function () { return {} }
  Component.options._originDataFn = ComponentData

  Component.options.data = function () {
    const data = ComponentData.call(this, this)
    if (this.$ssrContext) {
      asyncData = this.$ssrContext.asyncData[Component.cid]
    }
    return { ...data, ...asyncData }
  }

  Component.options.__hasNuxtData = true

  if (Component._Ctor && Component._Ctor.options) {
    Component._Ctor.options.data = Component.options.data
  }
}

export function sanitizeComponent (Component) {
  // If Component already sanitized
  if (Component.options && Component._Ctor === Component) {
    return Component
  }
  if (!Component.options) {
    Component = Vue.extend(Component) // fix issue #6
    Component._Ctor = Component
  } else {
    Component._Ctor = Component
    Component.extendOptions = Component.options
  }
  // If no component name defined, set file path as name, (also fixes #5703)
  if (!Component.options.name && Component.options.__file) {
    Component.options.name = Component.options.__file
  }
  return Component
}

export function getMatchedComponents (route, matches = false, prop = 'components') {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m[prop]).map((key) => {
      matches && matches.push(index)
      return m[prop][key]
    })
  }))
}

export function getMatchedComponentsInstances (route, matches = false) {
  return getMatchedComponents(route, matches, 'instances')
}

export function flatMapComponents (route, fn) {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m.components).reduce((promises, key) => {
      if (m.components[key]) {
        promises.push(fn(m.components[key], m.instances[key], m, key, index))
      } else {
        delete m.components[key]
      }
      return promises
    }, [])
  }))
}

export function resolveRouteComponents (route, fn) {
  return Promise.all(
    flatMapComponents(route, async (Component, instance, match, key) => {
      // If component is a function, resolve it
      if (typeof Component === 'function' && !Component.options) {
        try {
          Component = await Component()
        } catch (error) {
          // Handle webpack chunk loading errors
          // This may be due to a new deployment or a network problem
          if (
            error &&
            error.name === 'ChunkLoadError' &&
            typeof window !== 'undefined' &&
            window.sessionStorage
          ) {
            const timeNow = Date.now()
            const previousReloadTime = parseInt(window.sessionStorage.getItem('nuxt-reload'))

            // check for previous reload time not to reload infinitely
            if (!previousReloadTime || previousReloadTime + 60000 < timeNow) {
              window.sessionStorage.setItem('nuxt-reload', timeNow)
              window.location.reload(true /* skip cache */)
            }
          }

          throw error
        }
      }
      match.components[key] = Component = sanitizeComponent(Component)
      return typeof fn === 'function' ? fn(Component, instance, match, key) : Component
    })
  )
}

export async function getRouteData (route) {
  if (!route) {
    return
  }
  // Make sure the components are resolved (code-splitting)
  await resolveRouteComponents(route)
  // Send back a copy of route with meta based on Component definition
  return {
    ...route,
    meta: getMatchedComponents(route).map((Component, index) => {
      return { ...Component.options.meta, ...(route.matched[index] || {}).meta }
    })
  }
}

export async function setContext (app, context) {
  // If context not defined, create it
  if (!app.context) {
    app.context = {
      isStatic: process.static,
      isDev: false,
      isHMR: false,
      app,
      store: app.store,
      payload: context.payload,
      error: context.error,
      base: app.router.options.base,
      env: {"NUXT_ENV_KLIPY_API_KEY":"ga1hKHp1GAjhLOmx37MtlHtb9160e1KOO2XGtNW3rPpAKNpKfKAmo3kD6V8xbCQM","NUXT_ENV_API_OIDC":"https://auth.skade.io","NUXT_ENV_OIDC_CLIENT":"rop.client","NUXT_ENV_OIDC_REDIRECT":"https://app.skade.io","NUXT_ENV_OIDC_SCOPE":"openid profile offline_access IdentityServerApi","NUXT_ENV_POST_LOGOUT_REDIRECT_URL":"https://app.skade.io/?redirectToLogin=true","NUXT_ENV_API_URL":"https://api.skade.io","NUXT_ENV_ASSETS_URL":"https://api.skade.io/media/","NUXT_ENV_GIPHY_URI":"https://giphy.com/embed/","NUXT_ENV_KLIPY_API_URL":"https://api.klipy.com/api/v1","NUXT_ENV_ORIGIN":"https://app.skade.io","NUXT_ENV_SENTRY_DSN":"https://3473afdf64649a103311d882f5569712@sentry.skade.io/2","NUXT_ENV_SENTRY_ENABLED":"true","NUXT_ENV_INDEX_PAGE_REDIRECT":"https://www.skade.io/","NUXT_ENV_TOS_URL":"https://www.skade.io/politique-de-confidentialite","NUXT_ENV_PRIVACY_POLICY_URL":"https://www.skade.io/conditions-dutilisation","NUXT_ENV_ABOUT_US_URL":"https://www.skade.io/a-propos","NUXT_ENV_LEGAL_NOTICE_URL":"https://www.skade.io/mention-legales","NUXT_ENV_MARKETING_PAGE_URL":"https://www.skade.io","NUXT_ENV_GTM_ID":"GTM-TDZ9MBJ7","NUXT_ENV_GTM_DATALAYER_PUSH_ENABLED":"true","NUXT_ENV_GTM_DEBUG_ENABLED":"false","NUXT_ENV_GTM_LOGGING_ENABLED":"false","NUXT_ENV_LEAGUE_REGULATION_BASE_URI":"https://www.skade.io/","BUILD_NO_CACHE":"true","npm_package_devDependencies_ts_node":"^10.9.1","npm_package_dependencies_nuxt_svg_loader":"^1.2.0","npm_package_devDependencies__nuxtjs_eslint_module":"^3.1.0","npm_package_devDependencies__nuxt_typescript_build":"^2.1.0","npm_package_dependencies_apexcharts":"^4.0.0","npm_package_devDependencies__nuxtjs_dotenv":"^1.4.1","npm_package_devDependencies__nuxt_types":"^2.15.8","npm_config_version_commit_hooks":"true","npm_config_user_agent":"yarn/1.22.19 npm/? node/v16.20.2 linux x64","NODE_VERSION":"16.20.2","SENTRY_AUTH_TOKEN":"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","npm_package_dependencies_vue_apexcharts":"^1.6.2","npm_config_bin_links":"true","HOSTNAME":"f7c8f45aff10","YARN_VERSION":"1.22.19","npm_node_execpath":"/usr/local/bin/node","npm_package_dependencies__nuxtjs_toast":"^3.3.1","npm_config_init_version":"1.0.0","SHLVL":"4","npm_package_dependencies_vue_virtual_scroller":"1.1.2","HOME":"/root","npm_package_devDependencies__typescript_eslint_parser":"^5.60.1","npm_package_dependencies_vuex":"^3.6.2","npm_package_dependencies_vue_multiselect":"^2.1.6","npm_package_dependencies_chart_js":"2.9.4","NUXT_HOST":"0.0.0.0","npm_package_devDependencies_eslint_config_prettier":"^8.8.0","npm_package_dependencies_vue_spinner":"^1.0.4","npm_package_dependencies_vue_mq":"^1.0.1","npm_config_init_license":"MIT","YARN_WRAP_OUTPUT":"false","npm_package_devDependencies__types_axios":"^0.14.0","npm_config_version_tag_prefix":"v","npm_package_dependencies_v_click_outside":"^3.1.2","npm_package_dependencies_html2canvas":"^1.4.1","npm_package_dependencies_debounce_async":"^0.0.2","npm_package_scripts_bundle_analyze":"nuxt build --analyze","npm_package_dependencies_video_js":"^8.6.0","npm_package_dependencies_oidc_client_ts":"2.4.0","npm_package_dependencies__nuxtjs_gtm":"^2.4.0","npm_package_dependencies__tailwindcss_typography":"^0.4.0","npm_package_dependencies__sentry_vue":"^7.113.0","npm_package_description":"La plateforme sociale des investisseurs en bourse & cryptomonnaies","npm_package_readmeFilename":"README.md","npm_package_devDependencies__types_pulltorefreshjs":"^0.1.7","npm_package_dependencies_nuxt":"^2.15.7","npm_package_dependencies_jwt_decode":"^3.1.2","NUXT_PORT":"8080","npm_package_devDependencies_prettier":"^2.8.8","npm_package_devDependencies__nuxtjs_style_resources":"^1.1.0","npm_package_dependencies_v_emoji_picker":"^2.3.3","npm_package_scripts_dev":"nuxt","VERSION":"1.5.4","npm_package_dependencies_portal_vue":"2.1.7","npm_package_devDependencies_node_sass":"6.0.0","npm_package_dependencies_postcss_inline_svg":"^6.0.0","npm_package_private":"true","npm_package_devDependencies__typescript_eslint_eslint_plugin":"^5.60.1","npm_package_dependencies_postcss_svgo":"^6.0.0","npm_package_scripts_lint":"eslint --ext .ts,.js,.vue .","npm_config_registry":"https://registry.yarnpkg.com","npm_package_devDependencies__types_lodash":"^4.14.195","npm_package_dependencies_material_icons":"^1.13.1","npm_package_dependencies_core_js":"^3.13.1","npm_package_dependencies__sentry_browser":"^8.47.0","npm_package_scripts_start":"nuxt start","npm_config_ignore_scripts":"","npm_config_version":"1.22.19","npm_package_devDependencies__faker_js_faker":"^8.0.2","PATH":"/tmp/yarn--1792659627821-0.9632602636334249:/skade-pwa/node_modules/.bin:/usr/local/share/.config/yarn/link/node_modules/.bin:/usr/local/libexec/lib/node_modules/npm/bin/node-gyp-bin:/usr/local/lib/node_modules/npm/bin/node-gyp-bin:/usr/local/bin/node_modules/npm/bin/node-gyp-bin:/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin","NODE":"/usr/local/bin/node","npm_package_dependencies_swiper":"^11.2.8","npm_package_scripts_deploy_dev":"wsl cd deploy ; ansible-playbook -i hosts deploy_project.yml -e 'project_name=PWA stage=dev ports=8081:8080 mode=development'","npm_package_name":"skade.io","npm_package_dependencies_swiped_events":"^1.2.0","npm_package_dependencies_quill_mention":"^3.0.3","npm_package_dependencies__braid_vue_formulate":"^2.5.2","npm_package_scripts_deploy_prod":"wsl cd deploy ; ansible-playbook -i hosts deploy_project.yml -e 'project_name=PWA stage=master ports=8080:8080 mode=production'","npm_package_dependencies_quill":"^1.3.7","npm_package_devDependencies_eslint":"^8.42.0","npm_package_devDependencies__vue_eslint_config_typescript":"^11.0.3","npm_package_dependencies_vue_carousel":"^0.18.0","npm_package_dependencies_pulltorefreshjs":"^0.1.22","npm_package_scripts_sb_build":"nuxt storybook build","SENTRY_PROJECT":"pwa","npm_lifecycle_script":"nuxt build","npm_package_devDependencies_dotenv":"^16.3.1","npm_package_devDependencies__nuxtjs_pwa":"^3.3.5","npm_package_dependencies_vue_js_toggle_button":"^1.3.3","npm_package_dependencies_navigator_locks":"^0.8.6","npm_package_scripts_sb":"nuxt storybook","npm_package_devDependencies__vue_eslint_config_prettier":"^7.1.0","npm_package_dependencies_vue_infinite_loading":"^2.4.5","npm_package_dependencies_highcharts":"^9.1.2","npm_config_version_git_message":"v%s","npm_lifecycle_event":"build","npm_package_devDependencies__rushstack_eslint_patch":"^1.3.2","npm_package_dependencies_vuedraggable":"^2.24.3","npm_package_dependencies__nuxtjs_dayjs":"^1.4.0","npm_package_version":"1.5.3","npm_config_argv":"{\"remain\":[],\"cooked\":[\"run\",\"build\"],\"original\":[\"build\"]}","npm_package_devDependencies__nuxtjs_tailwindcss":"4.2.1","npm_package_dependencies_highcharts_vue":"^1.4.0","npm_package_dependencies_fuse_js":"^6.6.2","npm_package_scripts_build":"nuxt build","npm_package_dependencies_vue_cropperjs":"^4.2.0","npm_package_dependencies_lodash":"^4.17.21","npm_package_devDependencies_sass_loader":"^10.1.1","npm_package_devDependencies_eslint_plugin_tailwindcss":"^2.0.1","npm_package_dependencies_vue_chartjs":"^3.5.1","npm_config_version_git_tag":"true","npm_config_version_git_sign":"","SENTRY_ENVIRONMENT":"production","npm_package_dependencies_lightweight_charts":"^4.0.1","npm_package_dependencies__microsoft_signalr":"^5.0.7","npm_package_dependencies__egjs_vue_flicking":"^4.11.3","npm_package_scripts_generate":"nuxt generate","npm_package_license":"UNLICENSED","npm_config_strict_ssl":"true","npm_package_dependencies_vue_star_rating":"^1.7.0","PWD":"/skade-pwa","npm_execpath":"/opt/yarn-v1.22.19/bin/yarn.js","SENTRY_ORG":"skade","npm_package_devDependencies_postcss":"^8.3.5","npm_package_devDependencies_eslint_plugin_vue":"^9.15.0","npm_package_dependencies_vue_avatar":"^2.3.3","npm_package_dependencies_v_tooltip":"^2.1.3","npm_config_save_prefix":"^","npm_config_ignore_optional":"","npm_package_dependencies__nuxtjs_axios":"^5.13.6","DOTENV_PATH":".env.production","NODE_ENV":"development","npm_package_dependencies_tailwindcss":"2.2.19","npm_package_dependencies_babel_loader":"8.0.0","INIT_CWD":"/skade-pwa","npm_package_devDependencies__nuxtjs_storybook":"^4.3.2","npm_package_dependencies_vue_clamp":"^0.4.1","npm_package_dependencies_string_similarity":"^4.0.4","npm_package_dependencies_nuxt_i18n":"^6.27.0","_applied":"true","VUE_ENV":"server"}
    }
    // Only set once

    if (context.req) {
      app.context.req = context.req
    }
    if (context.res) {
      app.context.res = context.res
    }

    if (context.ssrContext) {
      app.context.ssrContext = context.ssrContext
    }
    app.context.redirect = (status, path, query) => {
      if (!status) {
        return
      }
      app.context._redirected = true
      // if only 1 or 2 arguments: redirect('/') or redirect('/', { foo: 'bar' })
      let pathType = typeof path
      if (typeof status !== 'number' && (pathType === 'undefined' || pathType === 'object')) {
        query = path || {}
        path = status
        pathType = typeof path
        status = 302
      }
      if (pathType === 'object') {
        path = app.router.resolve(path).route.fullPath
      }
      // "/absolute/route", "./relative/route" or "../relative/route"
      if (/(^[.]{1,2}\/)|(^\/(?!\/))/.test(path)) {
        app.context.next({
          path,
          query,
          status
        })
      } else {
        path = withQuery(path, query)
        if (process.server) {
          app.context.next({
            path,
            status
          })
        }
        if (process.client) {
          // https://developer.mozilla.org/en-US/docs/Web/API/Location/assign
          window.location.assign(path)

          // Throw a redirect error
          throw new Error('ERR_REDIRECT')
        }
      }
    }
    if (process.server) {
      app.context.beforeNuxtRender = fn => context.beforeRenderFns.push(fn)
      app.context.beforeSerialize = fn => context.beforeSerializeFns.push(fn)
    }
    if (process.client) {
      app.context.nuxtState = window.__NUXT__
    }
  }

  // Dynamic keys
  const [currentRouteData, fromRouteData] = await Promise.all([
    getRouteData(context.route),
    getRouteData(context.from)
  ])

  if (context.route) {
    app.context.route = currentRouteData
  }

  if (context.from) {
    app.context.from = fromRouteData
  }

  if (context.error) {
    app.context.error = context.error
  }

  app.context.next = context.next
  app.context._redirected = false
  app.context._errored = false
  app.context.isHMR = false
  app.context.params = app.context.route.params || {}
  app.context.query = app.context.route.query || {}
}

export function middlewareSeries (promises, appContext, renderState) {
  if (!promises.length || appContext._redirected || appContext._errored || (renderState && renderState.aborted)) {
    return Promise.resolve()
  }
  return promisify(promises[0], appContext)
    .then(() => {
      return middlewareSeries(promises.slice(1), appContext, renderState)
    })
}

export function promisify (fn, context) {
  let promise
  if (fn.length === 2) {
    // fn(context, callback)
    promise = new Promise((resolve) => {
      fn(context, function (err, data) {
        if (err) {
          context.error(err)
        }
        data = data || {}
        resolve(data)
      })
    })
  } else {
    promise = fn(context)
  }

  if (promise && promise instanceof Promise && typeof promise.then === 'function') {
    return promise
  }
  return Promise.resolve(promise)
}

// Imported from vue-router
export function getLocation (base, mode) {
  if (mode === 'hash') {
    return window.location.hash.replace(/^#\//, '')
  }

  base = decodeURI(base).slice(0, -1) // consideration is base is normalized with trailing slash
  let path = decodeURI(window.location.pathname)

  if (base && path.startsWith(base)) {
    path = path.slice(base.length)
  }

  const fullPath = (path || '/') + window.location.search + window.location.hash

  return normalizeURL(fullPath)
}

// Imported from path-to-regexp

/**
 * Compile a string to a template function for the path.
 *
 * @param  {string}             str
 * @param  {Object=}            options
 * @return {!function(Object=, Object=)}
 */
export function compile (str, options) {
  return tokensToFunction(parse(str, options), options)
}

export function getQueryDiff (toQuery, fromQuery) {
  const diff = {}
  const queries = { ...toQuery, ...fromQuery }
  for (const k in queries) {
    if (String(toQuery[k]) !== String(fromQuery[k])) {
      diff[k] = true
    }
  }
  return diff
}

export function normalizeError (err) {
  let message
  if (!(err.message || typeof err === 'string')) {
    try {
      message = JSON.stringify(err, null, 2)
    } catch (e) {
      message = `[${err.constructor.name}]`
    }
  } else {
    message = err.message || err
  }
  return {
    ...err,
    message,
    statusCode: (err.statusCode || err.status || (err.response && err.response.status) || 500)
  }
}

/**
 * The main path matching regexp utility.
 *
 * @type {RegExp}
 */
const PATH_REGEXP = new RegExp([
  // Match escaped characters that would otherwise appear in future matches.
  // This allows the user to escape special characters that won't transform.
  '(\\\\.)',
  // Match Express-style parameters and un-named parameters with a prefix
  // and optional suffixes. Matches appear as:
  //
  // "/:test(\\d+)?" => ["/", "test", "\d+", undefined, "?", undefined]
  // "/route(\\d+)"  => [undefined, undefined, undefined, "\d+", undefined, undefined]
  // "/*"            => ["/", undefined, undefined, undefined, undefined, "*"]
  '([\\/.])?(?:(?:\\:(\\w+)(?:\\(((?:\\\\.|[^\\\\()])+)\\))?|\\(((?:\\\\.|[^\\\\()])+)\\))([+*?])?|(\\*))'
].join('|'), 'g')

/**
 * Parse a string for the raw tokens.
 *
 * @param  {string}  str
 * @param  {Object=} options
 * @return {!Array}
 */
function parse (str, options) {
  const tokens = []
  let key = 0
  let index = 0
  let path = ''
  const defaultDelimiter = (options && options.delimiter) || '/'
  let res

  while ((res = PATH_REGEXP.exec(str)) != null) {
    const m = res[0]
    const escaped = res[1]
    const offset = res.index
    path += str.slice(index, offset)
    index = offset + m.length

    // Ignore already escaped sequences.
    if (escaped) {
      path += escaped[1]
      continue
    }

    const next = str[index]
    const prefix = res[2]
    const name = res[3]
    const capture = res[4]
    const group = res[5]
    const modifier = res[6]
    const asterisk = res[7]

    // Push the current path onto the tokens.
    if (path) {
      tokens.push(path)
      path = ''
    }

    const partial = prefix != null && next != null && next !== prefix
    const repeat = modifier === '+' || modifier === '*'
    const optional = modifier === '?' || modifier === '*'
    const delimiter = res[2] || defaultDelimiter
    const pattern = capture || group

    tokens.push({
      name: name || key++,
      prefix: prefix || '',
      delimiter,
      optional,
      repeat,
      partial,
      asterisk: Boolean(asterisk),
      pattern: pattern ? escapeGroup(pattern) : (asterisk ? '.*' : '[^' + escapeString(delimiter) + ']+?')
    })
  }

  // Match any characters still remaining.
  if (index < str.length) {
    path += str.substr(index)
  }

  // If the path exists, push it onto the end.
  if (path) {
    tokens.push(path)
  }

  return tokens
}

/**
 * Prettier encoding of URI path segments.
 *
 * @param  {string}
 * @return {string}
 */
function encodeURIComponentPretty (str, slashAllowed) {
  const re = slashAllowed ? /[?#]/g : /[/?#]/g
  return encodeURI(str).replace(re, (c) => {
    return '%' + c.charCodeAt(0).toString(16).toUpperCase()
  })
}

/**
 * Encode the asterisk parameter. Similar to `pretty`, but allows slashes.
 *
 * @param  {string}
 * @return {string}
 */
function encodeAsterisk (str) {
  return encodeURIComponentPretty(str, true)
}

/**
 * Escape a regular expression string.
 *
 * @param  {string} str
 * @return {string}
 */
function escapeString (str) {
  return str.replace(/([.+*?=^!:${}()[\]|/\\])/g, '\\$1')
}

/**
 * Escape the capturing group by escaping special characters and meaning.
 *
 * @param  {string} group
 * @return {string}
 */
function escapeGroup (group) {
  return group.replace(/([=!:$/()])/g, '\\$1')
}

/**
 * Expose a method for transforming tokens into the path function.
 */
function tokensToFunction (tokens, options) {
  // Compile all the tokens into regexps.
  const matches = new Array(tokens.length)

  // Compile all the patterns before compilation.
  for (let i = 0; i < tokens.length; i++) {
    if (typeof tokens[i] === 'object') {
      matches[i] = new RegExp('^(?:' + tokens[i].pattern + ')$', flags(options))
    }
  }

  return function (obj, opts) {
    let path = ''
    const data = obj || {}
    const options = opts || {}
    const encode = options.pretty ? encodeURIComponentPretty : encodeURIComponent

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (typeof token === 'string') {
        path += token

        continue
      }

      const value = data[token.name || 'pathMatch']
      let segment

      if (value == null) {
        if (token.optional) {
          // Prepend partial segment prefixes.
          if (token.partial) {
            path += token.prefix
          }

          continue
        } else {
          throw new TypeError('Expected "' + token.name + '" to be defined')
        }
      }

      if (Array.isArray(value)) {
        if (!token.repeat) {
          throw new TypeError('Expected "' + token.name + '" to not repeat, but received `' + JSON.stringify(value) + '`')
        }

        if (value.length === 0) {
          if (token.optional) {
            continue
          } else {
            throw new TypeError('Expected "' + token.name + '" to not be empty')
          }
        }

        for (let j = 0; j < value.length; j++) {
          segment = encode(value[j])

          if (!matches[i].test(segment)) {
            throw new TypeError('Expected all "' + token.name + '" to match "' + token.pattern + '", but received `' + JSON.stringify(segment) + '`')
          }

          path += (j === 0 ? token.prefix : token.delimiter) + segment
        }

        continue
      }

      segment = token.asterisk ? encodeAsterisk(value) : encode(value)

      if (!matches[i].test(segment)) {
        throw new TypeError('Expected "' + token.name + '" to match "' + token.pattern + '", but received "' + segment + '"')
      }

      path += token.prefix + segment
    }

    return path
  }
}

/**
 * Get the flags for a regexp from the options.
 *
 * @param  {Object} options
 * @return {string}
 */
function flags (options) {
  return options && options.sensitive ? '' : 'i'
}

export function addLifecycleHook(vm, hook, fn) {
  if (!vm.$options[hook]) {
    vm.$options[hook] = []
  }
  if (!vm.$options[hook].includes(fn)) {
    vm.$options[hook].push(fn)
  }
}

export const urlJoin = joinURL

export const stripTrailingSlash = withoutTrailingSlash

export const isSamePath = _isSamePath

export function setScrollRestoration (newVal) {
  try {
    window.history.scrollRestoration = newVal;
  } catch(e) {}
}
